import type { SuggesterStation } from './types'
import { searchStations } from './search'

/**
 * Outcome of a {@link SearchService.search} call.
 */
export interface SearchResult {
  /** Matched stations, in rank order. */
  readonly stations: readonly SuggesterStation[]
  /**
   * `true` when the result came from the bundled **offline** index
   * because the online backend call failed (spec §15). `false` for a
   * successful online call, and also `false` for a bundled-only
   * configuration (no online provider wired) — there was no failure.
   */
  readonly offline: boolean
}

/**
 * An online search backend. Resolves with the matched stations, or
 * rejects when the network / backend is unavailable (→ offline fallback).
 *
 * No real Metro search backend exists yet; the host injects a stub
 * implementation. The abstraction is in place so a real endpoint can be
 * dropped in later without touching the Suggester.
 */
export type OnlineSearchProvider = (query: string) => Promise<readonly SuggesterStation[]>

/**
 * Configuration for {@link createSearchService}.
 */
export interface SearchServiceConfig {
  /**
   * Online backend. When omitted the service is bundled-only — every
   * query resolves against `offlineData` with `offline: false` (no
   * failure occurred).
   */
  readonly online?: OnlineSearchProvider
  /**
   * Bundled offline station catalogue. Searched when the online call
   * fails, and also the sole source in the bundled-only configuration.
   * Ships with every client so search works on first launch with no
   * connection (spec §15).
   */
  readonly offlineData: readonly SuggesterStation[]
}

/**
 * Search abstraction (spec §13) — online backend with a bundled offline
 * fallback.
 */
export interface SearchService {
  /**
   * Run a query. Tries the online backend first; on failure re-runs the
   * query against the bundled offline index and flags `offline: true`.
   */
  search: (query: string) => Promise<SearchResult>
}

/**
 * Build a {@link SearchService}.
 *
 * - With an `online` provider: online-first, offline fallback on failure.
 * - Without one: bundled-only — the offline index is the single source
 *   and `offline` stays `false` (no failure to report).
 */
export function createSearchService(config: SearchServiceConfig): SearchService {
  const { online, offlineData } = config

  const offlineSearch = (query: string): readonly SuggesterStation[] =>
    searchStations(query, offlineData)

  return {
    search: async (query) => {
      if (online === undefined) {
        return { stations: offlineSearch(query), offline: false }
      }
      try {
        const stations = await online(query)
        return { stations, offline: false }
      } catch {
        return { stations: offlineSearch(query), offline: true }
      }
    },
  }
}
